// Contiguous US states (2-letter abbreviation) → most-populous IANA timezone.
// For states split across multiple zones, uses the zone covering the majority of the population.
const STATE_TO_IANA = {
    AL: 'America/Chicago',
    AZ: 'America/Phoenix',
    AR: 'America/Chicago',
    CA: 'America/Los_Angeles',
    CO: 'America/Denver',
    CT: 'America/New_York',
    DE: 'America/New_York',
    DC: 'America/New_York',
    FL: 'America/New_York',
    GA: 'America/New_York',
    ID: 'America/Boise',
    IL: 'America/Chicago',
    IN: 'America/Indiana/Indianapolis',
    IA: 'America/Chicago',
    KS: 'America/Chicago',
    KY: 'America/New_York',
    LA: 'America/Chicago',
    ME: 'America/New_York',
    MD: 'America/New_York',
    MA: 'America/New_York',
    MI: 'America/Detroit',
    MN: 'America/Chicago',
    MS: 'America/Chicago',
    MO: 'America/Chicago',
    MT: 'America/Denver',
    NE: 'America/Chicago',
    NV: 'America/Los_Angeles',
    NH: 'America/New_York',
    NJ: 'America/New_York',
    NM: 'America/Denver',
    NY: 'America/New_York',
    NC: 'America/New_York',
    ND: 'America/Chicago',
    OH: 'America/New_York',
    OK: 'America/Chicago',
    OR: 'America/Los_Angeles',
    PA: 'America/New_York',
    RI: 'America/New_York',
    SC: 'America/New_York',
    SD: 'America/Chicago',
    TN: 'America/Chicago',
    TX: 'America/Chicago',
    UT: 'America/Denver',
    VT: 'America/New_York',
    VA: 'America/New_York',
    WA: 'America/Los_Angeles',
    WV: 'America/New_York',
    WI: 'America/Chicago',
    WY: 'America/Denver',
};

export function getTimezoneForState(stateCode) {
    if (!stateCode || typeof stateCode !== 'string') return null;
    return STATE_TO_IANA[stateCode.toUpperCase()] || null;
}

// Returns the short timezone abbreviation (e.g. "EST", "PDT") for a given IANA timezone
// at a specific date. Handles DST automatically.
export function getTimezoneAbbr(ianaTimezone, date) {
    if (!ianaTimezone) return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: ianaTimezone,
            timeZoneName: 'short',
        }).formatToParts(date || new Date());
        const tzPart = parts.find(p => p.type === 'timeZoneName');
        return tzPart ? tzPart.value : '';
    } catch {
        return '';
    }
}

// Converts "YYYY-MM-DDTHH:mm" + IANA timezone → UTC epoch milliseconds.
// Uses Intl.DateTimeFormat to detect the timezone's UTC offset at that date,
// so DST transitions are handled correctly.
export function showingToEpochMs(isoString, ianaTimezone) {
    if (!isoString || !ianaTimezone) return null;
    const m = typeof isoString === 'string' && isoString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return null;

    const year = parseInt(m[1]);
    const month = parseInt(m[2]) - 1;
    const day = parseInt(m[3]);
    const hour = parseInt(m[4]);
    const minute = parseInt(m[5]);

    // Interpret the entered time as UTC initially
    const asUTC = Date.UTC(year, month, day, hour, minute);

    // Format that UTC instant in the target timezone to find the offset
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const parts = Object.fromEntries(
        formatter.formatToParts(new Date(asUTC)).map(p => [p.type, p.value])
    );

    // What time the target timezone shows when it's `asUTC` in UTC
    const tzHour = parts.hour === '24' ? 0 : parseInt(parts.hour);
    const inTZ = Date.UTC(parseInt(parts.year), parseInt(parts.month) - 1, parseInt(parts.day), tzHour, parseInt(parts.minute));

    // The offset = what the timezone shows minus what UTC actually was
    const offsetMs = inTZ - asUTC;

    // The true UTC epoch = our "local" time minus the offset
    return asUTC - offsetMs;
}
